/**
 * AI System - Definitions for AI worms in the multiplayer worm game
 *
 * This file defines:
 * - AI personality types and their behavior weights
 * - AI states for the state machine
 * - Named AI characters with their personalities
 * - Leviathan boss constants
 */

// =============================================================================
// AI PERSONALITY TYPES
// =============================================================================

/**
 * Personality type names - 4 Tier System
 * - Collector (30%): Passive food gatherers, easy targets
 * - Hunter (50%): Balanced AI, standard threat
 * - Apex (19%): Aggressive hunters, real danger
 * - Legendary (1%): Boss-like AI, only 1 can exist (LEVIATHAN)
 */
export type PersonalityType = 'Collector' | 'Hunter' | 'Apex' | 'Legendary';

/**
 * AI Personality configuration
 * Controls how the AI makes decisions
 */
export interface AIPersonality {
  /** Personality type identifier */
  type: PersonalityType;
  /** How much the AI prioritizes hunting other players (0-1) */
  huntWeight: number;
  /** How much the AI prioritizes collecting food (0-1) */
  foodWeight: number;
  /** How willing the AI is to take risks (0-1) */
  riskTolerance: number;
}

/**
 * Predefined personality configurations - 4 Tier System
 */
export const AI_PERSONALITIES: Record<PersonalityType, AIPersonality> = {
  /**
   * Collector (30% spawn rate)
   * Passive food gatherers, easy targets, variety
   */
  Collector: {
    type: 'Collector',
    huntWeight: 0.15,
    foodWeight: 0.85,
    riskTolerance: 0.25,
  },
  /**
   * Hunter (50% spawn rate)
   * Balanced AI, standard threat, core gameplay
   */
  Hunter: {
    type: 'Hunter',
    huntWeight: 0.50,
    foodWeight: 0.50,
    riskTolerance: 0.50,
  },
  /**
   * Apex (19% spawn rate)
   * Aggressive hunters, real danger, uses sprint
   */
  Apex: {
    type: 'Apex',
    huntWeight: 0.80,
    foodWeight: 0.20,
    riskTolerance: 0.85,
  },
  /**
   * Legendary (1% spawn rate)
   * Boss-like AI, only 1 can exist (LEVIATHAN)
   */
  Legendary: {
    type: 'Legendary',
    huntWeight: 0.95,
    foodWeight: 0.05,
    riskTolerance: 0.95,
  },
};

/**
 * Spawn rates for each personality tier (must sum to 1.0)
 */
export const AI_SPAWN_RATES: Record<PersonalityType, number> = {
  Collector: 0.30, // 30%
  Hunter: 0.50,    // 50%
  Apex: 0.19,      // 19%
  Legendary: 0.01, // 1%
};

// =============================================================================
// LEVIATHAN CONSTANTS
// =============================================================================

/** Chance for LEVIATHAN to spawn when spawning a new AI (1%) */
export const LEVIATHAN_SPAWN_CHANCE = 0.01;

/** Minimum number of players required for LEVIATHAN to spawn */
export const LEVIATHAN_MIN_PLAYERS = 5;

/** Respawn delay for LEVIATHAN after death (3 minutes in ms) */
export const LEVIATHAN_RESPAWN_DELAY = 180000;

/** Number of segments LEVIATHAN spawns with */
export const LEVIATHAN_SPAWN_SEGMENTS = 150;

/** Bonus multiplier for food drops when LEVIATHAN dies (50% extra) */
export const LEVIATHAN_BONUS_DROPS = 1.5;

// =============================================================================
// AI STATES
// =============================================================================

/**
 * AI State machine states
 */
export enum AIState {
  /** Seeking the best nearby food (value/distance ratio) */
  HUNT_FOOD = 'HUNT_FOOD',
  /** Chasing a player when larger and close */
  HUNT_PLAYER = 'HUNT_PLAYER',
  /** Running from threats (bigger worms facing the AI) */
  EVADE = 'EVADE',
  /** Going for nearby powerups */
  COLLECT_POWERUP = 'COLLECT_POWERUP',
  /** Returning to center when near boundary */
  FLEE_EDGE = 'FLEE_EDGE',
}

// =============================================================================
// AI CONFIGURATION
// =============================================================================

/**
 * AI runtime configuration
 */
export interface AIConfig {
  /** Think interval in milliseconds (not every frame) */
  thinkIntervalMs: number;
  /** Distance at which AI considers edge dangerous */
  edgeDangerDistance: number;
  /** Maximum distance to search for food */
  foodSearchRadius: number;
  /** Distance at which AI starts considering hunting players */
  huntRadius: number;
  /** Distance at which AI detects threats */
  threatDetectionRadius: number;
  /** Score multiplier required to consider hunting a player */
  huntScoreRatio: number;
  /** Minimum score to allow sprinting */
  minSprintScore: number;
  /** How many prediction frames ahead to aim when hunting */
  predictionFrames: number;
}

/**
 * Default AI configuration values
 * Tuned for smart, aggressive AI behavior
 */
export const DEFAULT_AI_CONFIG: AIConfig = {
  thinkIntervalMs: 50, // Faster reactions
  edgeDangerDistance: 400, // More cautious near edge
  foodSearchRadius: 600, // Wider food search
  huntRadius: 700, // Detect players from further away
  threatDetectionRadius: 300, // More aware of danger
  huntScoreRatio: 0.9, // Hunt similar-sized or smaller players (was 1.2)
  minSprintScore: 200, // AI can sprint when hunting (AI starts with 80-160 score)
  predictionFrames: 24, // Faster prediction
};

// =============================================================================
// AI CHARACTERS
// =============================================================================

/**
 * AI Character definition
 */
export interface AICharacter {
  /** Display name for the AI */
  name: string;
  /** Personality type */
  personalityType: PersonalityType;
  /** Personality configuration (resolved from type) */
  personality: AIPersonality;
  /** Description for reference */
  description: string;
  /** Whether this is a legendary (boss) character */
  isLegendary?: boolean;
}

/**
 * All AI characters available in the game
 * 31 named AI characters (30 regular + 1 LEVIATHAN)
 *
 * Distribution:
 * - Collectors (8): Passive food gatherers
 * - Hunters (12): Balanced, standard threat
 * - Apex (10): Aggressive hunters
 * - Legendary (1): LEVIATHAN boss
 */
export const AI_CHARACTERS: AICharacter[] = [
  // ==========================================================================
  // LEGENDARY (1 character) - Boss-like AI, only 1 can exist
  // ==========================================================================
  {
    name: 'LEVIATHAN',
    personalityType: 'Legendary',
    personality: AI_PERSONALITIES.Legendary,
    description: 'The ultimate predator',
    isLegendary: true,
  },

  // ==========================================================================
  // APEX (10 characters) - Aggressive hunters, real danger, uses sprint
  // ==========================================================================
  { name: 'Slither', personalityType: 'Apex', personality: AI_PERSONALITIES.Apex, description: 'Adaptive hunter' },
  { name: 'Viper', personalityType: 'Apex', personality: AI_PERSONALITIES.Apex, description: 'Swift striker' },
  { name: 'Crusher', personalityType: 'Apex', personality: AI_PERSONALITIES.Apex, description: 'Size dominator' },
  { name: 'Shadow', personalityType: 'Apex', personality: AI_PERSONALITIES.Apex, description: 'Stealthy pursuer' },
  { name: 'Venom', personalityType: 'Apex', personality: AI_PERSONALITIES.Apex, description: 'Precise striker' },
  { name: 'Hunter', personalityType: 'Apex', personality: AI_PERSONALITIES.Apex, description: 'Relentless tracker' },
  { name: 'Constrictor', personalityType: 'Apex', personality: AI_PERSONALITIES.Apex, description: 'Wrap specialist' },
  { name: 'Anaconda', personalityType: 'Apex', personality: AI_PERSONALITIES.Apex, description: 'Giant hunter' },
  { name: 'Python', personalityType: 'Apex', personality: AI_PERSONALITIES.Apex, description: 'Patient predator' },
  { name: 'Fang', personalityType: 'Apex', personality: AI_PERSONALITIES.Apex, description: 'Bold attacker' },

  // ==========================================================================
  // HUNTERS (12 characters) - Balanced AI, standard threat, core gameplay
  // ==========================================================================
  { name: 'Chompy', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Opportunistic' },
  { name: 'Noodle', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Flexible tactician' },
  { name: 'Zippy', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Speed demon' },
  { name: 'Twister', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Direction master' },
  { name: 'Squirm', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Unpredictable' },
  { name: 'Slinky', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Flowing movement' },
  { name: 'Speedy', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Fast and agile' },
  { name: 'Munch', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Value seeker' },
  { name: 'Rattler', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Warning striker' },
  { name: 'Coil', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Encircling master' },
  { name: 'Sidewinder', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Lateral mover' },
  { name: 'Fangface', personalityType: 'Hunter', personality: AI_PERSONALITIES.Hunter, description: 'Aggressive ambusher' },

  // ==========================================================================
  // COLLECTORS (8 characters) - Passive food gatherers, easy targets, variety
  // ==========================================================================
  { name: 'Nibbles', personalityType: 'Collector', personality: AI_PERSONALITIES.Collector, description: 'Steady grower' },
  { name: 'Wiggles', personalityType: 'Collector', personality: AI_PERSONALITIES.Collector, description: 'Careful survivor' },
  { name: 'Scales', personalityType: 'Collector', personality: AI_PERSONALITIES.Collector, description: 'Food finder' },
  { name: 'Gulp', personalityType: 'Collector', personality: AI_PERSONALITIES.Collector, description: 'Endless appetite' },
  { name: 'Crawler', personalityType: 'Collector', personality: AI_PERSONALITIES.Collector, description: 'Steady climber' },
  { name: 'Swallow', personalityType: 'Collector', personality: AI_PERSONALITIES.Collector, description: 'Quick consumer' },
  { name: 'Glider', personalityType: 'Collector', personality: AI_PERSONALITIES.Collector, description: 'Smooth operator' },
  { name: 'Earthworm', personalityType: 'Collector', personality: AI_PERSONALITIES.Collector, description: 'Humble survivor' },
];

/**
 * Get a random AI character based on spawn rates
 * Uses weighted random selection based on AI_SPAWN_RATES
 * Note: LEVIATHAN is excluded from random selection (use getLeviathan() directly)
 */
export function getRandomAICharacter(): AICharacter {
  const roll = Math.random();
  let cumulative = 0;

  // Determine which tier to pick from (excluding Legendary)
  let targetType: PersonalityType = 'Hunter'; // Default fallback

  // Collector: 0.30 (0.00 - 0.30)
  cumulative += AI_SPAWN_RATES.Collector;
  if (roll < cumulative) {
    targetType = 'Collector';
  } else {
    // Hunter: 0.50 (0.30 - 0.80)
    cumulative += AI_SPAWN_RATES.Hunter;
    if (roll < cumulative) {
      targetType = 'Hunter';
    } else {
      // Apex: 0.19 (0.80 - 0.99)
      // Legendary is handled separately, so Apex gets the remaining probability
      targetType = 'Apex';
    }
  }

  // Get all characters of the target type (excluding legendary)
  const candidates = AI_CHARACTERS.filter(
    (char) => char.personalityType === targetType && !char.isLegendary
  );

  // Pick a random character from the candidates
  return candidates[Math.floor(Math.random() * candidates.length)]!;
}

/**
 * Get AI character by name
 */
export function getAICharacterByName(name: string): AICharacter | undefined {
  return AI_CHARACTERS.find((char) => char.name === name);
}

/**
 * Get the LEVIATHAN character
 */
export function getLeviathan(): AICharacter {
  return AI_CHARACTERS.find((char) => char.isLegendary)!;
}

/**
 * Check if a character is the LEVIATHAN
 */
export function isLeviathan(character: AICharacter): boolean {
  return character.isLegendary === true;
}

/**
 * Get all characters of a specific personality type
 */
export function getCharactersByType(type: PersonalityType): AICharacter[] {
  return AI_CHARACTERS.filter((char) => char.personalityType === type);
}
